import React from 'react';
import { Link } from 'react-router-dom';
import BlueCallToAction from '../components/common/BlueCallToAction';

/**
 * AboutPage component
 * Showcases company information and mission
 */
const AboutPage = () => {
  const benefits = [
    {
      title: "Close the loop",
      description: "Enable data to flow in real-time through every point of care and ensure that nothing is lost in the process to improve efficiency, effectiveness and positive outcomes."
    },
    {
      title: "Empower team collaboration",
      description: "Accelerate your team's ability to join forces, coordinate and share information in a secure environment while simplifying complex workflows."
    },
    {
      title: "Improve insights and become proactive",
      description: "Our solutions and tools leverage AI/ML to predict future outcomes, while real-time data and alerts can help you solve complex challenges in an automated and proactive way."
    },
    {
      title: "Protect health information and improve compliance",
      description: "Protect sensitive health data to support privacy and security while managing evolving compliance requirements."
    }
  ];

  const solutions = [
    {
      title: "Reduce administrative burden",
      description: "For physicians, nurses and revenue cycle office staff with process automation and tools"
    },
    {
      title: "Ease information overload",
      description: "With better and familiar user experience and targeted metrics"
    },
    {
      title: "Accelerate insights",
      description: "And decision making while real-time data and alerts"
    },
    {
      title: "Quickly recognize shifts",
      description: "Of encounter payor mixes to re-allocate staffing responsibilities"
    },
    {
      title: "Actionable revenue cycle intelligence",
      description: "From insightful metrics so your teams can be proactive and act fast"
    },
    {
      title: "Easily identify causes",
      description: "Of increases in Discharged Not Final Billed (DNFB) and Discharged Not Submitted to Payor (DNSP) metrics"
    }
  ];

  return (
    <div className="home-page">
      {/* Hero Section */}
      <section className="py-20" style={{
        background: `linear-gradient(to top, #FFC000 0%, #FFFFFF 3%, #FFFFFF 97%, #FFC000 100%)`
      }}>
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h1 className="text-5xl md:text-6xl font-bold mb-6 text-gray-900">
              <span style={{ color: '#FFC000' }}>Actionable</span> Healthcare Insights
            </h1>
            
            <p className="text-xl md:text-2xl mb-8 text-gray-700">
              Revenue cycle solutions that provide clarity for effective decision making and financial goal achievement.
              Nimble technology that learns, continuously adds value, and evolves with your business according to your needs.
            </p>

            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <Link 
                to="/demo"
                style={{ 
                  backgroundColor: '#0E3056',
                  color: 'white',
                  padding: '1rem 2rem',
                  borderRadius: '0.5rem',
                  fontWeight: '600',
                  transition: 'background-color 0.2s'
                }}
                className="hover:bg-opacity-90"
              >
                Request Demo
              </Link>
              <Link
                to="/contact"
                style={{ 
                  border: '2px solid #0E3056',
                  color: '#0E3056',
                  padding: '1rem 2rem',
                  borderRadius: '0.5rem',
                  fontWeight: '600',
                  transition: 'all 0.2s'
                }}
                className="hover:bg-[#0E3056] hover:text-white"
              >
                Contact Us
              </Link>
            </div>
          </div>
        </div>
      </section>

      {/* Mission Section */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl font-bold mb-8 text-gray-900 dark:text-white">
              Drive decision making, efficiency, effectiveness and outcomes
            </h2>
            <p className="text-lg text-gray-600 dark:text-gray-300 mb-12">
              Our solutions provide holistic visibility into your KPIs allowing you to understand performance from multiple vantage points.
              Our solutions and tools enable you to immediately act upon and address identified issues by translating the insights gained into action.
            </p>
          </div>
        </div>
      </section>

      {/* Benefits Section */}
      <section className="py-16 bg-gray-50 dark:bg-gray-900">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center text-gray-900 dark:text-white">
            Key Benefits
          </h2>
          <div className="grid md:grid-cols-2 gap-8 max-w-6xl mx-auto">
            {benefits.map((benefit, index) => (
              <div 
                key={index}
                className="bg-white dark:bg-gray-800 rounded-lg p-6 shadow-sm"
              >
                <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                  {benefit.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  {benefit.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Solutions Section */}
      <section className="py-16 bg-white dark:bg-gray-800">
        <div className="container mx-auto px-4">
          <h2 className="text-3xl font-bold mb-12 text-center text-gray-900 dark:text-white">
            Our Solutions
          </h2>
          <div className="grid md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {solutions.map((solution, index) => (
              <div 
                key={index}
                className="bg-gray-50 dark:bg-gray-700 rounded-lg p-6 shadow-sm"
              >
                <h3 className="text-xl font-semibold mb-4 text-gray-900 dark:text-white">
                  {solution.title}
                </h3>
                <p className="text-gray-600 dark:text-gray-300">
                  {solution.description}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Call-to-Action Section */}
      <BlueCallToAction 
        heading="Ready to transform your healthcare operations?"
        description="Experience how our AI solutions can drive better outcomes and efficiency."
        primaryButtonText="Request Demo"
        primaryButtonLink="/demo"
        secondaryButtonText="Contact Us"
        secondaryButtonLink="/contact"
      />
    </div>
  );
};

export default AboutPage;